import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Eye, EyeOff, Lock, Ambulance, ArrowRight, Mail, User, ChevronDown
} from "lucide-react";
import axios from "axios";
import { setPassengerSession } from "../../passengerAuth";
import "./Passenger.css";

const GENDER_OPTIONS = ["Male", "Female"];

export default function PassengerSignup() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    gender: "",
    password: "",
    confPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfPassword, setShowConfPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setError("");
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.confPassword) {
      setError("Passwords don't match.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/passengerRegisterWeb`,
        {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          gender: form.gender || "Unknown",
          password: form.password,
          confPassword: form.confPassword,
        }
      );

      if (res.status === 200 || res.status === 201) {
        // Redirect to email verification page with the provided email
        navigate(`/passenger/verify-email?email=${encodeURIComponent(form.email.trim())}`);
      } else {
        setError("Registration failed. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        "Registration failed. Please try again.";
      setError(msg);
      setLoading(false);
    }
  };

  const isValid =
    form.firstName && form.lastName && form.email &&
    form.password && form.confPassword;

  return (
    <div className="pax-login-wrapper">
      {/* LEFT PANEL */}
      <div className="pax-login-left">
        <div className="pax-login-left-content">
          <div className="pax-brand-icon">
            <Ambulance size={40} color="#fff" />
          </div>
          <h1 className="pax-brand-title">ለAmbulance</h1>
          <p className="pax-brand-subtitle">Passenger Portal</p>

          <div className="pax-features">
            <div className="pax-feature-item">
              <div className="pax-feature-icon"><Ambulance size={18} /></div>
              <div>
                <div className="pax-feature-label">Emergency Response</div>
                <div className="pax-feature-desc">Immediate medical assistance</div>
              </div>
            </div>
            <div className="pax-feature-item">
              <div className="pax-feature-icon"><Lock size={18} /></div>
              <div>
                <div className="pax-feature-label">Safe &amp; Secure</div>
                <div className="pax-feature-desc">Your data is always protected</div>
              </div>
            </div>
            <div className="pax-feature-item">
              <div className="pax-feature-icon"><Mail size={18} /></div>
              <div>
                <div className="pax-feature-label">Web Access</div>
                <div className="pax-feature-desc">No mobile app required</div>
              </div>
            </div>
          </div>

          <div className="pax-emergency-badge">
            <span className="pax-badge-dot" />
            Emergency Hotline: <strong>9117</strong>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="pax-login-right">
        <div className="pax-login-form-wrapper">
          <div className="pax-form-header">
            <h2>Create Account</h2>
            <p>Register with your email — no mobile app needed</p>
          </div>

          {error && (
            <div className="pax-error-alert">
              <span>⚠</span> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="pax-form">
            {/* Name row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="pax-form-group">
                <label>First Name</label>
                <div className="pax-input-wrapper">
                  <span className="pax-input-icon"><User size={16} /></span>
                  <input
                    type="text"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder="First name"
                    required
                    id="pax-signup-firstname"
                    autoComplete="given-name"
                  />
                </div>
              </div>
              <div className="pax-form-group">
                <label>Last Name</label>
                <div className="pax-input-wrapper">
                  <span className="pax-input-icon"><User size={16} /></span>
                  <input
                    type="text"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder="Last name"
                    required
                    id="pax-signup-lastname"
                    autoComplete="family-name"
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="pax-form-group">
              <label>Email Address</label>
              <div className="pax-input-wrapper">
                <span className="pax-input-icon"><Mail size={16} /></span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  id="pax-signup-email"
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Gender (optional) */}
            <div className="pax-form-group">
              <label>Gender <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span></label>
              <div className="pax-input-wrapper" style={{ position: "relative" }}>
                <span className="pax-input-icon"><ChevronDown size={16} /></span>
                <select
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  className="pax-select"
                  id="pax-signup-gender"
                >
                  <option value="">Select gender</option>
                  {GENDER_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>

            {/* Password */}
            <div className="pax-form-group">
              <label>Password</label>
              <div className="pax-input-wrapper">
                <span className="pax-input-icon"><Lock size={17} /></span>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="At least 6 chars, letters & numbers"
                  required
                  id="pax-signup-password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="pax-eye-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="pax-form-group">
              <label>Confirm Password</label>
              <div className="pax-input-wrapper">
                <span className="pax-input-icon"><Lock size={17} /></span>
                <input
                  type={showConfPassword ? "text" : "password"}
                  name="confPassword"
                  value={form.confPassword}
                  onChange={handleChange}
                  placeholder="Repeat your password"
                  required
                  id="pax-signup-confpassword"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="pax-eye-btn"
                  onClick={() => setShowConfPassword(!showConfPassword)}
                  tabIndex={-1}
                >
                  {showConfPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="pax-submit-btn"
              disabled={loading || !isValid}
              id="pax-signup-btn"
            >
              {loading ? (
                <span className="pax-btn-loading">
                  <span className="pax-spinner" /> Creating account…
                </span>
              ) : (
                <span className="pax-btn-content">
                  Create Account <ArrowRight size={18} />
                </span>
              )}
            </button>
          </form>

          <div className="pax-form-footer">
            <div className="pax-divider"><span>Already have an account?</span></div>
            <p style={{ textAlign: "center", marginTop: 12, fontSize: "0.9rem", color: "#475569" }}>
              <Link
                to="/passenger/login"
                style={{ color: "#1d4ed8", fontWeight: 600, textDecoration: "none" }}
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
